import React, { useEffect, useState } from 'react'
import { Field, Formik } from 'formik'
import { Collapse, FormGroup, FormLabel, Button, Form } from 'react-bootstrap'
import { Select } from '../../components/Select'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import _ from 'lodash'
import { transformToSelectOptions } from '../../utils/common'
import { getStateOptions, FileLifecycleState } from '../../services/filter'

interface TableFilterProps {
  config: any
  properties?: any
  defaultFilter?: any
}

const TableFilter: React.FC<TableFilterProps> = ({ config, properties, defaultFilter }) => {
  const context = useServiceContext() as any
  const { service, api } = context
  const { translate } = context.filter || context.service.filter
  const tableData = useRcmTableData()
  const [allArtifact, setAllArtifact] = useState<any>({ values: [] })
  const [allSection, setAllSection] = useState<any>({ values: [] })
  const [filterOptions, setFilterOptions] = useState<any>({
    possibleStates: [],
    countries: [],
    sites: []
  })

  const completionTypeDataSet = [
    { value: 'UPLOADED', type: 'DOCUMENTS_UPLOADED', label: 'ETMF_COMPLETION_TYPE_UPLOADED_DOCUMENTS' },
    { value: 'COMPLETED', type: 'DOCUMENTS_FILED', label: 'ETMF_COMPLETION_TYPE_FILED_DOCUMENTS' }
  ]

  const renderCompletionTypeData = (completionTypeData: any, id: string, values: any) => (
    <Form.Check
      name="filter.completionType"
      className="mb5 w100p"
      value={completionTypeData.value}
      checked={values.filter.completionType === completionTypeData.value}
      type={'radio'}
      onClick={() => {
        context.store.formRefStaging?.setFieldValue('filter.completionType', completionTypeData.value)
        context.store.formRefStaging?.setFieldValue('filter.type', completionTypeData.type)
      }}
      id={id}
      label={translate(completionTypeData.label)}
    />
  )

  const loadCountries = (filter: any) => {
    if (!filterOptions.countries?.length) {
      api.etmf.getSelectionsForStagingAreaFilter('COUNTRY', config.contextId, config.contextId).then((data: any[]) => {
        if (data.length > 1) {
          data = [
            {
              fsiId: null,
              fsiName: translate('ETMF_LABEL_SELECT_ALL_COUNTRIES')
            }
          ].concat(data)
        }
        setFilterOptions((prev: any) => ({
          ...prev,
          countries: transformToSelectOptions(data, 'fsiId', 'fsiName')
        }))
      })
    } else {
      loadCountriesWhenLevelIsSite({ ...filter })
    }
  }

  const loadCountriesWhenLevelIsSite = (filter: any) => {
    if (filter.level === 'SITE') {
      if (filter.country?.length > 0) {
        filter.country.forEach((individualCountry: any) => {
          onCountryChange(individualCountry)
        })
      } else {
        onCountryChange(filter.country)
      }
    }
  }

  const isCountryEnabled = (level: string) => ['COUNTRY', 'SITE'].includes(level)
  const showSite = (level: string) => level === 'SITE'

  const onCountryChange = (countryValue: any) => {
    api.etmf
      .getSelectionsForStagingAreaFilter('SITE', config.contextId, _.map(countryValue, 'value'))
      .then((data: any[]) => {
        if (data.length > 1) {
          data = [
            {
              fsiId: null,
              fsiName: translate('ETMF_LABEL_SELECT_ALL_SITES')
            }
          ].concat(data)
        }
        setFilterOptions((prev: any) => ({
          ...prev,
          sites: transformToSelectOptions(data, 'fsiId', 'fsiName')
        }))
      })
  }

  const getFilteredSections = (filterByVal: any) => {
    const zoneNo = filterByVal ? filterByVal.match(/^\d{2}/) : null
    if (Array.isArray(zoneNo) && zoneNo[0]) {
      setAllSection((prev: any) => ({
        ...prev?.section,
        values: properties?.section?.usedValues?.filter((val: string) => val.startsWith(zoneNo[0] + '.')) || []
      }))
    } else {
      setAllSection((prev: any) => ({
        ...prev?.section,
        values: properties?.section ? properties.section.usedValues : []
      }))
    }
  }

  const getFilteredArtifacts = (filterByVal: any, isZoneSelected: boolean) => {
    let zoneOrSection = null
    if (isZoneSelected) {
      zoneOrSection = filterByVal ? filterByVal.match(/^\d{2}/) : null
    } else {
      zoneOrSection = filterByVal ? filterByVal.match(/^\d{2}.{3}/) : null
    }
    if (Array.isArray(zoneOrSection) && zoneOrSection[0]) {
      setAllArtifact({
        values: properties?.artifact?.values?.filter((val: string) => val.startsWith(zoneOrSection[0] + '.')) || []
      })
    } else {
      setAllArtifact((prev: any) => ({
        ...prev?.artifact,
        values: properties?.artifact ? properties.artifact.values : []
      }))
    }
  }

  useEffect(() => {
    setFilterOptions((prev: any) => ({
      ...prev,
      possibleStates: transformToSelectOptions(
        Object.entries(getStateOptions(['COMPLETED', 'EXPIRED', 'REJECTED', 'OBSOLETE'] as FileLifecycleState[])).map(
          ([key, value]) => ({ id: key, value: translate(value) })
        ),
        'id',
        'value'
      )
    }))
    getFilteredArtifacts(null, false)
    getFilteredSections(null)
  }, [properties])

  const doFilter = (values: any) => {
    const filterValues = { ...values }
    tableData.applyFilter(filterValues)
  }

  return (
    <Collapse in={tableData.isFilterCollapsed}>
      <div className="filter-wrapper">
        <div className="row">
          <div className="col-sm-12 col-lg-12">
            <Formik
              validate={(values) => {
                const errors: any = {}
                if (
                  (values.filter.level === 'COUNTRY' || values.filter.level === 'SITE') &&
                  values.filter.country?.length < 1
                ) {
                  errors.country = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
                }
                if (values.filter.level === 'SITE' && values.filter.site?.length < 1) {
                  errors.site = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
                }
                return errors
              }}
              enableReinitialize={true}
              innerRef={(el: any) => {
                context.store.formRefStaging = el
              }}
              initialValues={defaultFilter}
              onSubmit={(values) => doFilter(values)}
            >
              {({ values, setFieldValue, handleSubmit, resetForm, errors }) => (
                <>
                  {config?.isStudyUser && (
                    <div className="row">
                      <div className="col-xs-4">
                        <FormGroup className="mb0 w100p form-group" controlId="df7008df-5bda-48f2-8b5a-fd7cc7e71b9b">
                          <FormLabel className="control-label">{translate('STAGING_DOCS_DOC_NAME')}</FormLabel>
                          <Field
                            className="form-control"
                            type="text"
                            name="filter.placeholderName"
                            maxLength={255}
                            id="39022300-99b2-446b-94d0-9d27ad450d0e"
                          />
                        </FormGroup>
                        <FormGroup className="mb0" controlId="abe43645-48ed-4c35-ad76-ff66f2d30d92">
                          <FormLabel>{translate('STATE')}</FormLabel>
                          <Select
                            name="filter.state"
                            value={values.filter.state}
                            options={filterOptions.possibleStates ?? []}
                            onChange={(option: any) => {
                              setFieldValue('filter.state', option ? option.value : null)
                            }}
                            id="79b1053c-769c-4cbd-9e2f-431483a3b23e"
                            isClearable={false}
                          />
                        </FormGroup>
                        {config.mode === 'user' && (
                          <FormGroup controlId="3c51a94b-6ec1-4646-aff4-2c5d466cfe89">
                            <FormLabel>{translate('ETMF_DOC_COMPLETION_TYPE')}</FormLabel>
                            {completionTypeDataSet?.map((cmpType) =>
                              renderCompletionTypeData(
                                cmpType,
                                `0db6f87e-08f0-4707-9da3-cb4427dccd6d-${cmpType.value}`,
                                values
                              )
                            )}
                          </FormGroup>
                        )}
                      </div>
                      <div className="col-xs-4">
                        <FormGroup controlId="f94addd7-ad1f-4a77-9130-7b0d2f850147">
                          <FormLabel>{translate('EMTF_LEVEL')}</FormLabel>
                          <Field
                            name="filter.level"
                            className="mb5 w100p"
                            value={'ALL'}
                            checked={values.filter.level === 'ALL'}
                            as="input"
                            type="radio"
                            onChange={() => {
                              setFieldValue('filter.level', 'ALL')
                              setFieldValue('filter.site', [])
                              setFieldValue('filter.country', [])
                            }}
                            id={`2a8d4bde-da8f-45e1-ab87-766449122e0d-ALL`}
                          />
                          {translate('ALL')}
                          <Field
                            name="filter.level"
                            className="mb5 w100p"
                            value={'STUDY'}
                            checked={values.filter.level === 'STUDY'}
                            as="input"
                            type="radio"
                            onChange={() => {
                              setFieldValue('filter.level', 'STUDY')
                              setFieldValue('filter.site', [])
                              setFieldValue('filter.country', [])
                            }}
                            id={`2a8d4bde-da8f-45e1-ab87-766449122e0d-STUDY`}
                          />
                          {translate('EMTF_MD_STUDY')}
                          <Field
                            name="filter.level"
                            className="mb5 w100p"
                            value={'COUNTRY'}
                            checked={values.filter.level === 'COUNTRY'}
                            as="input"
                            type="radio"
                            onChange={() => {
                              setFieldValue('filter.level', 'COUNTRY')
                              setFieldValue('filter.site', [])
                              loadCountries(values.filter)
                            }}
                            id={`2a8d4bde-da8f-45e1-ab87-766449122e0d-COUNTRY`}
                          />
                          {translate('EMTF_MD_COUNTRY')}
                          <Field
                            name="filter.level"
                            className="mb5 w100p"
                            value={'SITE'}
                            checked={values.filter.level === 'SITE'}
                            as="input"
                            type="radio"
                            onChange={() => {
                              setFieldValue('filter.level', 'SITE')
                              loadCountries(values.filter)
                            }}
                            id={`2a8d4bde-da8f-45e1-ab87-766449122e0d-SITE`}
                          />
                          {translate('EMTF_MD_SITE')}
                        </FormGroup>
                        {isCountryEnabled(values.filter.level) && (
                          <FormGroup className="w100p mt20" controlId="81ce0954-141f-484f-8bd3-ddb1cc6dad23">
                            <FormLabel>{translate('EMTF_MD_COUNTRY')}</FormLabel>
                            <Select
                              name="filter.country"
                              value={values.filter.country}
                              options={filterOptions.countries ?? []}
                              onChange={(option: any) => {
                                setFieldValue('filter.country', option ? _.map(option, 'value') : [])
                                onCountryChange(option)
                              }}
                              isMulti
                              id="b3e48aff-520c-480a-88b2-5ce107653b0b"
                              isClearable={false}
                              placeholder={translate('ETMF_LABEL_SELECT_COUNTRY')}
                            />
                            {errors.country && <div className="text-danger">{translate(errors.country)}</div>}
                          </FormGroup>
                        )}
                        {showSite(values.filter.level) && (
                          <FormGroup className="w100p" controlId="008c35cb-5414-4e9a-b30a-4072a4790b38">
                            <FormLabel>{translate('EMTF_MD_SITE')}</FormLabel>
                            <Select
                              isDisabled={!values.filter.country?.length}
                              name="filter.site"
                              value={values.filter.site}
                              options={filterOptions.sites ?? []}
                              onChange={(option: any) => {
                                setFieldValue('filter.site', option ? _.map(option, 'value') : [])
                              }}
                              id="007577cc-8f41-4d42-8783-ec48d11dbd2f"
                              isClearable={true}
                              isMulti
                              placeholder={translate('SELECT_SITE')}
                            />
                            {errors.site && <div className="text-danger">{translate(errors.site)}</div>}
                          </FormGroup>
                        )}
                      </div>
                      <div className="col-xs-4">
                        <FormGroup className="w100p" controlId="27355641-6b21-466e-867e-5d25676ad9bb">
                          <FormLabel>{translate('EMTF_ZONE')}</FormLabel>
                          <Select
                            name="filter.zone"
                            value={values.filter.zone}
                            options={[
                              { value: '', label: translate('ETMF_ALL_ZONES') },
                              ...(transformToSelectOptions(properties?.zone?.usedValues) ?? [])
                            ]}
                            onChange={(option: any) => {
                              getFilteredArtifacts(option.value, true)
                              getFilteredSections(option.value)
                              setFieldValue('filter.zone', option ? option.value : null)
                            }}
                            id="67f93ef2-6b82-49dc-b466-cc6c135e7069"
                            isClearable={false}
                          />
                        </FormGroup>
                        <FormGroup className="w100p" controlId="3b3f2474-8172-4702-9467-8b16cb497afd">
                          <FormLabel>{translate('EMTF_SECTION')}</FormLabel>
                          <Select
                            name="filter.section"
                            value={values.filter.section}
                            options={[
                              { value: '', label: translate('ETMF_ALL_SECTIONS') },
                              ...(transformToSelectOptions(allSection?.values) ?? [])
                            ]}
                            onChange={(option: any) => {
                              getFilteredArtifacts(option.value, false)
                              setFieldValue('filter.section', option ? option.value : null)
                            }}
                            id="f3a6a8ae-54c5-410d-acaf-d1dd1012f3b7"
                            isClearable={false}
                          />
                        </FormGroup>
                        <FormGroup className="w100p" controlId="3411d654-56fb-4508-a1c9-b89530fe6199">
                          <FormLabel>{translate('EMTF_MILESTONE')}</FormLabel>
                          <Select
                            name="filter.milestone"
                            value={values.filter.milestone}
                            options={[
                              { value: '', label: translate('ETMF_ALL_MILESTONES') },
                              ...(transformToSelectOptions(properties?.milestone?.usedValues) ?? [])
                            ]}
                            onChange={(option: any) => {
                              setFieldValue('filter.milestone', option ? option.value : null)
                            }}
                            id="e47c81b6-cf3b-43b5-bc25-3915ea3eeb80"
                            isClearable={false}
                          />
                        </FormGroup>
                        <FormGroup className="w100p" controlId="27fbbcd6-078c-4919-ad68-f0801567dcb4">
                          <FormLabel>{translate('TABLE_TH_ARTIFACT_NO')}</FormLabel>
                          <Select
                            name="filter.artifacts"
                            value={values.filter.artifacts}
                            options={transformToSelectOptions(allArtifact?.values) ?? []}
                            onChange={(option: any) => {
                              setFieldValue('filter.artifacts', option ? option.value : null)
                            }}
                            placeholder={translate('SELECT_ARTIFACT(S)_PLACEHOLDER')}
                            id="201f6452-d7f1-493e-9b91-5b813542781b"
                            isClearable={false}
                          />
                        </FormGroup>
                      </div>
                    </div>
                  )}
                  {!config.isStudyUser && (
                    <div className="row">
                      <div className="col-xs-4">
                        <FormGroup className="mb0 w100p form-group" controlId="e9a248ec-df85-42d4-94bb-936a4965db94">
                          <FormLabel className="control-label">{translate('STAGING_DOCS_DOC_NAME')}</FormLabel>
                          <Field
                            className={'form-control field-control'}
                            type="text"
                            name="filter.placeholderName"
                            maxLength={255}
                            id="5617e403-ef86-4ad4-abe6-51c9e1c25c3d"
                          />
                        </FormGroup>
                        <FormGroup className="w100p" controlId="63b9f53c-5a4f-42f0-8bd4-1fc7dffba5eb">
                          <FormLabel>{translate('EMTF_SECTION')}</FormLabel>
                          <Select
                            name="filter.section"
                            value={values.filter.section}
                            options={[
                              { value: '', label: translate('ETMF_ALL_SECTIONS') },
                              ...(transformToSelectOptions(allSection?.values) ?? [])
                            ]}
                            onChange={(option: any) => {
                              getFilteredArtifacts(option.value, false)
                              setFieldValue('filter.section', option ? option.value : null)
                            }}
                            id="93be34d4-2500-43f0-ac0a-1cd74aa1f966"
                            isClearable={false}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-xs-4">
                        <FormGroup className="w100p" controlId="233f9381-094c-49f3-8501-0ffb13c363ab">
                          <FormLabel>{translate('EMTF_MILESTONE')}</FormLabel>
                          <Select
                            name="filter.milestone"
                            value={values.filter.milestone}
                            options={[
                              { value: '', label: translate('ETMF_ALL_MILESTONES') },
                              ...(transformToSelectOptions(properties?.milestone?.usedValues) ?? [])
                            ]}
                            onChange={(option: any) => {
                              setFieldValue('filter.milestone', option ? option.value : null)
                            }}
                            id="f95adb52-6466-45fc-9e6e-a93ca0c65b20"
                            isClearable={false}
                          />
                        </FormGroup>
                        {config.mode === 'user' && (
                          <FormGroup className="w100p" controlId="7c5966e5-cf3f-41dc-8ca8-36fa59705b2b">
                            <FormLabel>{translate('ETMF_DOC_COMPLETION_TYPE')}</FormLabel>
                            {completionTypeDataSet?.map((cmpType) =>
                              renderCompletionTypeData(
                                cmpType,
                                `d0aa588b-ce41-4ddc-b3be-fb27f840db9f-${cmpType.value}`,
                                values
                              )
                            )}
                          </FormGroup>
                        )}
                      </div>
                      <div className="col-xs-4">
                        <FormGroup className="mb0" controlId="c335ad06-cbab-4ce6-9980-b09bbb395650">
                          <FormLabel>{translate('STATE')}</FormLabel>
                          <Select
                            name="filter.state"
                            value={values.filter.state}
                            options={filterOptions.possibleStates ?? []}
                            onChange={(option: any) => {
                              setFieldValue('filter.state', option ? option.value : null)
                            }}
                            id="7d9b3796-76c1-4c6b-8e90-437a74d57df3"
                            isClearable={false}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-xs-4">
                        <FormGroup className="w100p" controlId="bcc76ab3-d46b-48e6-8019-6fddabeb73af">
                          <FormLabel>{translate('EMTF_ZONE')}</FormLabel>
                          <Select
                            name="filter.zone"
                            value={values.filter.zone}
                            options={[
                              { value: '', label: translate('ETMF_ALL_ZONES') },
                              ...(transformToSelectOptions(properties?.zone?.usedValues) ?? [])
                            ]}
                            onChange={(option: any) => {
                              getFilteredArtifacts(option.value, true)
                              getFilteredSections(option.value)
                              setFieldValue('filter.zone', option ? option.value : null)
                            }}
                            id="ddff7458-9940-4292-9df5-fddd07be0d45"
                            isClearable={false}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-xs-4">
                        <FormGroup className="w100p" controlId="b09ae9bd-9cf8-4859-85f1-7089a331f6e3">
                          <FormLabel>{translate('TABLE_TH_ARTIFACT_NO')}</FormLabel>
                          <Select
                            name="filter.artifacts"
                            value={values.filter.artifacts}
                            options={transformToSelectOptions(allArtifact?.values) ?? []}
                            onChange={(option: any) => {
                              setFieldValue('filter.artifacts', option ? option.value : null)
                            }}
                            id="051691a8-5438-443c-b709-a06255f817c2"
                            isClearable={false}
                            placeholder={translate('SELECT_ARTIFACT(S)_PLACEHOLDER')}
                          />
                        </FormGroup>
                      </div>
                    </div>
                  )}
                  <div className="clearfix">
                    <div className="pull-left mt10">
                      <Button
                        data-action={'filter'}
                        disabled={tableData.isLoading}
                        className="btn btn-primary"
                        onClick={(e: React.FormEvent) => {
                          e.preventDefault()
                          handleSubmit()
                        }}
                        id="ddf4ad25-3b7e-462c-a8e2-3d1a1904d41b"
                      >
                        {translate('BTN_FILTER')}
                      </Button>
                      <Button
                        style={{ color: '#3374c8' }}
                        data-action={'reset'}
                        className="btn btn-chita btn-link"
                        onClick={() => {
                          getFilteredArtifacts(null, false)
                          getFilteredSections(null)
                          resetForm()
                        }}
                        id="26eda1b2-b2e9-4549-9e9a-5d9b0f19656e"
                      >
                        {translate('BTN_CLEAR_FILTER')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
